import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import log from 'loglevel'
import pluralize from 'pluralize'
import { parse } from 'query-string'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useCopyToClipboard } from 'usehooks-ts'
import { getAllReceptorTemplates } from 'src/xgenerated'
import type { Receptor } from 'src/xgenerated'
import {
  downloadEvidenceTable,
  formatEvidence,
  generatePdfBlob,
  generatePdfBuffer,
} from 'src/components/ModalForms/Evidence/ViewEvidenceForm/ViewEvidenceForm.utils'
import { Markup } from 'src/components/Reusable/Text/Markup'
import { Evidence } from '@trustero/trustero-api-web/lib/receptor_v1/receptor_pb'
import { RECEPTOR_TYPE } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { DeleteSVG, DownloadArrowSVG } from 'src/components/Icons/Basic'
import {
  EvidenceGroup,
  EVIDENCE_LINKAGE_TYPE,
  EvidenceIdFilter,
  EVIDENCE_READ_ONLY_FIELDS,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons'
import { isViewable } from 'src/components/ModalForms/Evidence/ViewEvidenceForm/ViewEvidenceForm.utils'
import { isTrArtifactMime } from 'src/components/ModalForms/Evidence/evidenceUtils'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { useFileTypeIcon } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { isFunction } from 'lodash'
import { documentBodyAsFile } from 'src/adapter/AttachmentAdapter'
import { useExclusions } from 'src/components/async/document/useDocument'
import { StyledDropdownToggle } from 'src/components/Reusable/StandardDropdown/StandardDropdown.styles'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { formatISODateTime } from 'src/Utils/formatDate'
import { showInfoToast, ToastPrompts } from 'src/Utils/helpers/toast'
import { MIME_TYPE, UrlFragments } from 'src/Utils/globalEnums'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { PERMISSIONS } from 'src/config/roleConfig'
import FileSaver from 'file-saver'
import { openInNewTab } from 'src/Utils/globalHelpers'
import { TextButton } from 'src/components/Reusable/Buttons/TextButton'
import { useModalState } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { EvidenceAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import {
  ItemTitle,
  ShowPageSectionHeader,
} from 'src/components/PageLayout/ShowPage'
import {
  generatePermalink,
  linkWithSearch,
} from 'src/components/PageLayout/Permalink'
import { EditableShowPageTitle } from 'src/components/Reusable/ShowPage/Title/EditableShowPageTitle'
import { useCurrentEvidenceId } from 'src/context/FormContext/CurrentEvidenceContext'
import {
  DropdownToggleButton,
  IconButton,
  IconButtonVariant,
  PrimaryButton,
  StandardButtonSize,
} from 'src/components/Reusable/Buttons'
import {
  Control,
  CONTROL_READ_ONLY_FIELDS,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { useCurrentControl } from 'src/context/FormContext'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import { Dropdown } from 'react-bootstrap'
import {
  useCanPerformAction,
  useHasRequiredPermissions,
} from 'src/app/AppAuth/AppAuth.hooks'
import {
  useUnlinkEvidenceFromControl,
  useUnlinkEvidenceFromRequest,
} from 'src/components/ModalForms/Evidence/useDeleteEvidence'
import { Changelog, useInfoPanelContext } from 'src/InfoPanel'
import { useControl } from 'src/components/async/model/control/useControl'
import { useDocumentRequest } from 'src/components/async/DocumentRequest/useDocumentRequests'
import { formatEvidenceCaption } from 'src/Utils/helpers/string.helpers'
import { useGrpcRevalidateByMethod } from 'src/components'
import {
  useHardEvidenceInvalidation,
  useInvalidateEvidence,
} from 'src/Utils/swrCacheInvalidation/useInvalidateEvidence'
import JSZip from 'jszip'
import { BoxLoader } from 'src/components/Reusable/ContentLoaders/BoxLoader'
import { useNewModel } from 'src/components/async/hooks/useNewModel'
import { GRC_PLATFORM } from '@trustero/trustero-api-web/lib/common/grc_platform_pb'
import { getIsFieldReadOnly } from 'src/Utils/helpers/grcplatform'
import { FloatingMenu } from 'src/components/Reusable/FloatingMenu/FloatingMenu'
import { FLOATING_MENU_PLACEMENT } from 'src/components/Reusable/FloatingMenu/FloatingMenu.constants'
import { FloatingMenuItem } from 'src/components/Reusable/FloatingMenu/FloatingMenu.components'
import { BasicDropdown } from 'src/components/Reusable/BasicDropdown/BasicDropdown'
import { BasicDropdownItem } from 'src/components/Reusable/BasicDropdown/BasicDropdown.types'
import {
  BasicDropdownMenu,
  BasicDropdownMenuItem,
} from 'src/components/Reusable/BasicDropdown/BasicDropdown.styles'
import palette from 'src/designSystem/variables/palette'
import {
  BASIC_DROPDOWN_ALIGN,
  BASIC_DROPDOWN_DROP,
} from 'src/components/Reusable/BasicDropdown/BasicDropdown.constants'
import {
  PopupButtonContainer,
  PopupButtonLabel,
  StyledEditIcon,
} from 'src/components/Reusable/Buttons/PopupButton/PopupButton.styles'
import { DocumentRequest } from '@trustero/trustero-api-web/lib/request/request_pb'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { EvidenceManagementPromiseClient } from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_grpc_web_pb'
import { ReactComponent as VerticalEllipsesIcon } from '../../components/Icons/assets/vertical-ellipses-icon.svg'
import {
  CopyLink,
  ViewDetails,
} from '../Controls/IndexItem/ControlsListItemMenu/ControlsListItemMenuComponents'
import { useImportedControlModalOnClick } from '../Controls/ControlsIndexGrid.hooks'
import {
  EvidenceShowPageIcon,
  ManualEvidenceContainer,
  EvidenceNoResultsTitle,
  EvidenceNoResultsContainer,
  EvidenceFileInfoContainer,
} from './evidence.styles'
import {
  useEvidenceConfigs,
  useEvidenceGroups,
  useEvidenceGroupsRequest,
  useEvidenceLinkNoFetch,
  useImportedEvidenceModalOnClick,
  useInvalidateEvidenceGroups,
  useUpdateCaption,
} from './evidence.hooks'
import {
  generateEvidencePdfWithExclusions,
  getEvidenceMimeType,
  getEvidenceVersionDropdownOptions,
  getIsLongText,
} from './evidence.helpers'
import { useCurrentEvidenceGroup } from './context/CurrentEvidenceGroupContext'
import { EvidenceTabs } from './show/tabs/EvidenceTabs'
import { EvidenceConfigType } from './evidence.constants'
import { EvidenceBodies } from './show/tabs/evidence/EvidenceBody'

export const AutomatedEvidenceIcon = (): JSX.Element => {
  return <EvidenceShowPageIcon icon={faWandMagicSparkles} />
}

export const EvidenceDocumentSection = ({
  evidence,
}: {
  evidence: EvidenceGroup
}): JSX.Element => (
  <>
    <ManualEvidenceSection evidence={evidence} />
    <EvidenceTabs evidence={evidence} />
  </>
)

export const DocumentRequestsTabNoResults = (): JSX.Element => {
  const { openModal } = useModalState()
  return (
    <EvidenceNoResultsContainer>
      <EvidenceNoResultsTitle>
        This evidence isn&apos;t linked to any requests.
      </EvidenceNoResultsTitle>
      <PrimaryButton
        size={StandardButtonSize.MEDIUM}
        requiredPermissions={[PERMISSIONS.EDIT]}
        text="Link to Requests"
        onClick={() => {
          openModal(ModalFormId.LINK_EVIDENCE_TO_REQUESTS)
        }}
      />
    </EvidenceNoResultsContainer>
  )
}

export const EvidenceVersionDropdown = ({
  evidenceId,
  evidenceInstanceId,
  fromControl,
  fromRequest,
}: {
  evidenceId: string
  evidenceInstanceId: string
  fromControl?: boolean
  fromRequest?: boolean
}): JSX.Element => {
  const [selectedItem, setSelectedItem] = useState<BasicDropdownItem | null>(
    null,
  )
  const { pageContext } = useParams()
  const navigate = useNavigate()
  const req = useEvidenceGroupsRequest()
  const newModel = useNewModel()
  const groupReq = useMemo(() => {
    if (evidenceId) {
      req.setEvidenceIdFilter(
        new EvidenceIdFilter().setEvidenceIdsList([evidenceId]),
      )
    }
    if (newModel) {
      req.setUseNewModel(true)
    }

    return req
  }, [req, evidenceId, newModel])

  const {
    data: groupsData,
    isLoading: groupsLoading,
    error: groupsError,
  } = useEvidenceGroups(groupReq)
  const isDataLoading = groupsLoading
  const noData = !groupsData
  const errorOccured = groupsError
  const items = useMemo(
    () => getEvidenceVersionDropdownOptions(groupsData) as BasicDropdownItem[],
    [groupsData],
  )

  useEffect(() => {
    if (items.length > 0) {
      setSelectedItem(
        items.find((item) => item.value === evidenceInstanceId) || items[0],
      )
    }
  }, [items, evidenceInstanceId])

  if (isDataLoading) {
    return <BoxLoader width={200} height={32} />
  } else if (noData) {
    if (errorOccured) {
      groupsError && log.error('Error loading evidence groups', groupsError)
    }
    return <></>
  }

  const onSelectCb = (item: BasicDropdownItem): void => {
    const selectedId = item.value
    const versionLink = linkWithSearch(
      `/${pageContext}/${EvidenceAbsoluteRoutes.SHOW}/${encodeURIComponent(
        selectedId,
      )}`,
    )
    const fragment = fromControl
      ? `#${UrlFragments.CONTROLS}`
      : fromRequest
      ? `#${UrlFragments.REQUESTS}`
      : ''
    const link = `${versionLink}${fragment}`
    navigate(link)
  }

  return (
    <BasicDropdown
      selectedItem={selectedItem || items[0]}
      items={items}
      onSelect={onSelectCb}
      drop={BASIC_DROPDOWN_DROP.DOWN}
      align={BASIC_DROPDOWN_ALIGN.END}
      borderColor={palette.neutral[100]}
      size="medium"
      offset={8}
    />
  )
}

export const ManualEvidenceBody = ({
  body,
  isLink,
}: {
  body: string
  isLink: boolean
}): JSX.Element =>
  isLink ? (
    <TextButton
      onClick={(e) => {
        e.preventDefault()
        openInNewTab(body.trim())
      }}
    >
      {body.trim()}
    </TextButton>
  ) : (
    <Markup markdown={body} />
  )

export const EvidenceShowTitle = ({
  evidence,
  isAutomated,
}: {
  evidence: EvidenceGroup
  isAutomated: boolean
}): JSX.Element => {
  const canUpdateEvidenceCaption = useCanPerformAction(
    CAPABILITY.CAN_UPDATE_EVIDENCE_CAPTION,
  )
  const mutate = useInvalidateEvidenceGroups()
  const updateEvidence = useUpdateCaption()
  const onUpdate = async (newCaption: string): Promise<void> => {
    try {
      await updateEvidence(evidence.getEvidenceId(), newCaption)
      await mutate()
    } catch (e) {
      log.error(`Error updating evidence caption`, e)
      showInfoToast(
        'Sorry, there was an error saving your changes. Please try again later.',
      )
    }
  }
  const grcPlatform = evidence.getGrcPlatform()?.getPlatform()
  const showModal = getIsFieldReadOnly({
    object: evidence.toObject(),
    field: EVIDENCE_READ_ONLY_FIELDS.EVIDENCE_CAPTION,
  })
  const onClickConfirmationModal = useImportedEvidenceModalOnClick({
    grcPlatform,
    showModal,
    evidenceId: evidence.getEvidenceId(),
    fields: [EVIDENCE_READ_ONLY_FIELDS.EVIDENCE_CAPTION],
  })

  const caption = evidence.getCaption()

  return isAutomated ? (
    <ItemTitle>{formatEvidenceCaption(caption, true)}</ItemTitle>
  ) : (
    <EditableShowPageTitle
      key={`${evidence.getId()}-${caption}`}
      name={caption}
      onUpdate={onUpdate}
      modelType={MODEL_TYPE.EVIDENCE}
      fieldName="caption"
      onClickConfirmationModal={onClickConfirmationModal}
      isDisabled={!canUpdateEvidenceCaption}
    />
  )
}

export const ManualEvidenceSection = ({
  evidence,
}: {
  evidence: EvidenceGroup
}): JSX.Element => {
  const contentId = evidence.getContentId()
  const mime = evidence.getMime()
  const actualMime = getEvidenceMimeType(mime)
  const isAutomated = evidence.getIsAutomated()
  const [evidenceId, setEvidenceId] = useState<string>(evidence.getId())
  const [configs, setConfigs] = useState<EvidenceConfigType[] | null>(null)
  const isEvidenceAutomated = evidence.getIsAutomated()
  const caption = evidence.getCaption()
  const isLinkedDocument = useMemo(
    () => evidence.getIsLinkedDocument(),
    [evidence],
  )
  const docImportLink = useMemo(() => evidence.getDocImportLink(), [evidence])
  const getEvidenceConfigs = useEvidenceConfigs(
    contentId,
    mime,
    isEvidenceAutomated,
    caption,
  )

  const FileTypeIcon = useFileTypeIcon({ mime: actualMime })

  useEffect(() => {
    if (isFunction(getEvidenceConfigs)) {
      const handleDocumentBody = async () => {
        try {
          const configs = await getEvidenceConfigs()
          if (!configs || !configs.length) return
          setConfigs(configs)
        } catch (err) {
          log.error(
            `Error downloading document body in EvidenceTabs - id: ${evidence.getId()}`,
            err,
          )
          showInfoToast(ToastPrompts.EVIDENCE_DOWNLOAD_ERROR)
        } finally {
          setEvidenceId(evidence.getId())
        }
      }
      handleDocumentBody()
    }

    return () => {
      setConfigs(null)
    }
  }, [evidence, getEvidenceConfigs, isLinkedDocument, docImportLink, mime])

  const isLongText = useMemo(
    () =>
      configs?.some((config) => getIsLongText(config.body, config.mimeType)),
    [configs],
  )

  const viewable = useMemo(
    () => configs?.some((config) => isViewable(config.mimeType)),
    [configs],
  )

  if (
    isAutomated ||
    !mime ||
    viewable ||
    isLongText ||
    isTrArtifactMime(mime)
  ) {
    return <></>
  }

  if (evidenceId !== evidence.getId()) {
    return <TableListLoader />
  }

  return (
    <>
      {configs ? (
        <ManualEvidenceContainer>
          <ShowPageSectionHeader> EVIDENCE </ShowPageSectionHeader>
          {isLinkedDocument && !viewable ? (
            <EvidenceFileInfoContainer
              onClick={() => openInNewTab(docImportLink)}
            >
              <FileTypeIcon width="120px" height="120px" />
              <p className="filename">{actualMime}</p>
            </EvidenceFileInfoContainer>
          ) : (
            <EvidenceBodies configs={configs} caption={caption} />
          )}
        </ManualEvidenceContainer>
      ) : (
        <TableListLoader />
      )}
    </>
  )
}

export const EvidenceRelevantDateButton = ({
  relevantDate,
  evidenceInstanceId,
  onUpdateRelevantDate,
}: {
  relevantDate: Timestamp
  evidenceInstanceId: string
  onUpdateRelevantDate?: () => void
}): JSX.Element => {
  const { openModal } = useModalState()
  const canUpdateEvidenceRelevantDate = useCanPerformAction(
    CAPABILITY.CAN_UPDATE_EVIDENCE_RELEVANT_DATE,
  )
  const { setEvidenceId, setRelevantDate, setOnUpdateRelevantDate } =
    useCurrentEvidenceId()

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      e.preventDefault()
      e.stopPropagation()
      if (!canUpdateEvidenceRelevantDate) return
      setEvidenceId(evidenceInstanceId)
      setRelevantDate(relevantDate)
      setOnUpdateRelevantDate(() => onUpdateRelevantDate)
      openModal(ModalFormId.RELEVANT_DATE)
    },
    [
      evidenceInstanceId,
      relevantDate,
      openModal,
      setEvidenceId,
      setRelevantDate,
      setOnUpdateRelevantDate,
      onUpdateRelevantDate,
      canUpdateEvidenceRelevantDate,
    ],
  )

  return (
    <PopupButtonContainer
      onClick={onClick}
      $size="small"
      $isDisabled={!canUpdateEvidenceRelevantDate}
    >
      <PopupButtonLabel $size="small">
        {formatISODateTime(relevantDate)}
      </PopupButtonLabel>
      {canUpdateEvidenceRelevantDate && <StyledEditIcon $size="small" />}
    </PopupButtonContainer>
  )
}

export const ControlEvidenceRelevantDateButton = ({
  evidenceId,
  relevantDate,
  controlModelId,
}: {
  evidenceId: string
  relevantDate: Timestamp
  controlModelId: string
}): JSX.Element => {
  const { openModal } = useModalState()
  const { setEvidenceId, setControlModelIds, setRelevantDate } =
    useCurrentEvidenceId()

  const onClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setEvidenceId(evidenceId)
    setControlModelIds([controlModelId])
    setRelevantDate(relevantDate)
    openModal(ModalFormId.RELEVANT_DATE)
  }

  return (
    <PopupButtonContainer onClick={onClick} $size="small">
      <PopupButtonLabel $size="small">
        {formatISODateTime(relevantDate)}
      </PopupButtonLabel>
      <StyledEditIcon />
    </PopupButtonContainer>
  )
}

export const ViewEvidenceDetails = ({
  evidenceInstanceId,
  controlId,
  requestId,
}: {
  evidenceInstanceId: string
  controlId: string
  requestId: string
}): JSX.Element => {
  const navigate = useNavigate()
  const evidenceLink = useEvidenceLinkNoFetch(
    evidenceInstanceId,
    controlId,
    requestId,
  )

  const onViewDetails = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      navigate(evidenceLink)
    },
    [navigate, evidenceLink],
  )

  return (
    <BasicDropdownMenuItem eventKey="1" onClick={onViewDetails}>
      View Details
    </BasicDropdownMenuItem>
  )
}

export const CopyEvidenceLink = ({
  evidenceInstanceId,
  controlId,
  requestId,
}: {
  evidenceInstanceId: string
  controlId: string
  requestId: string
}): JSX.Element => {
  const evidenceLink = useEvidenceLinkNoFetch(
    evidenceInstanceId,
    controlId,
    requestId,
  )
  const [_, setCopied] = useCopyToClipboard()

  const onCopyLink = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      setCopied(`${window.location.origin}${evidenceLink}`)
    },
    [evidenceLink, setCopied],
  )
  return (
    <BasicDropdownMenuItem eventKey="6" onClick={onCopyLink}>
      Copy Permalink
    </BasicDropdownMenuItem>
  )
}

export const ViewActivities = ({
  evidenceId,
}: {
  evidenceId: string
}): JSX.Element => {
  const { setInfoPanelState } = useInfoPanelContext()

  const onViewActivities = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setInfoPanelState({
        isShown: true,
        render: <Changelog subjectIds={[evidenceId]} />,
      })
    },
    [evidenceId, setInfoPanelState],
  )

  return (
    <BasicDropdownMenuItem eventKey="7" onClick={onViewActivities}>
      View Activities
    </BasicDropdownMenuItem>
  )
}

export const UnlinkEvidenceFromRequest = ({
  evidenceId,
  caption,
  requestId,
}: {
  evidenceId: string
  caption: string
  requestId: string
}): JSX.Element => {
  const mutateFunc = useInvalidateEvidence()
  const unlinkEvidence = useUnlinkEvidenceFromRequest({
    evidenceId,
    requestId,
    caption,
    isEvidence: true,
    mutateFunc,
  })
  return (
    <BasicDropdownMenuItem
      eventKey="8"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        unlinkEvidence()
      }}
    >
      Unlink Evidence
    </BasicDropdownMenuItem>
  )
}

export const UnlinkEvidenceFromControl = ({
  evidenceId,
  controlId,
  caption,
  onClickConfirmationModal,
  isViaRequest = false,
}: {
  evidenceId: string
  controlId: string
  caption: string
  onClickConfirmationModal?: (onClick: () => void) => void
  isViaRequest?: boolean
}): JSX.Element => {
  const mutateFunc = useHardEvidenceInvalidation()
  const unlinkEvidence = useUnlinkEvidenceFromControl({
    evidenceIds: [evidenceId],
    controlId,
    captions: [caption],
    mutateFunc,
    isEvidence: true,
    isViaRequest,
  })

  return (
    <BasicDropdownMenuItem
      eventKey="8"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        if (isFunction(onClickConfirmationModal)) {
          onClickConfirmationModal(() => setTimeout(unlinkEvidence, 0))
        } else {
          unlinkEvidence()
        }
      }}
    >
      Unlink Evidence
    </BasicDropdownMenuItem>
  )
}

export const EvidenceRowMenu = ({
  evidenceId,
  evidenceInstanceId,
  controlId,
  requestId,
  caption,
  linkageType,
  deleteFunc,
  grcPlatform,
  showImportedControlModal,
  isViaRequest,
}: {
  evidenceId: string
  evidenceInstanceId: string
  controlId: string
  requestId: string
  caption: string
  linkageType: EVIDENCE_LINKAGE_TYPE
  deleteFunc?: () => void
  grcPlatform?: GRC_PLATFORM
  showImportedControlModal?: boolean
  isViaRequest?: boolean
}): JSX.Element => {
  const canDeleteEvidence = useCanPerformAction(CAPABILITY.CAN_DELETE_EVIDENCE)
  const { data: controlData } = useControl(controlId)
  const { data: requestData } = useDocumentRequest(requestId)
  const control =
    controlData instanceof Control ? controlData.toObject() : undefined
  const request =
    requestData instanceof DocumentRequest ? requestData.toObject() : undefined
  const canLinkEvidenceControls = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_LINK_EVIDENCE_CONTROLS,
    control,
  )
  const canLinkEvidenceRequests = useCanPerformAction<DocumentRequest.AsObject>(
    CAPABILITY.CAN_LINK_REQUEST_EVIDENCE,
    request,
  )
  const onClickConfirmationModal = useImportedControlModalOnClick({
    controlId,
    fields: [CONTROL_READ_ONLY_FIELDS.CONTROL_EVIDENCE_MAP],
    grcPlatform,
    showModal: showImportedControlModal,
  })

  return (
    <Dropdown
      id={`evidence-row-menu-${evidenceId}`}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <Dropdown.Toggle as={DropdownToggleButton}>
        <VerticalEllipsesIcon />
      </Dropdown.Toggle>
      <BasicDropdownMenu>
        <ViewEvidenceDetails
          evidenceInstanceId={evidenceInstanceId}
          controlId={controlId}
          requestId={requestId}
        />
        {linkageType === EVIDENCE_LINKAGE_TYPE.REQUEST_LINK &&
        canLinkEvidenceRequests ? (
          <UnlinkEvidenceFromRequest
            evidenceId={evidenceId}
            requestId={requestId}
            caption={caption}
          />
        ) : linkageType === EVIDENCE_LINKAGE_TYPE.CONTROL_LINK &&
          canLinkEvidenceControls ? (
          <UnlinkEvidenceFromControl
            evidenceId={evidenceId}
            controlId={controlId}
            caption={caption}
            isViaRequest={isViaRequest}
            onClickConfirmationModal={onClickConfirmationModal}
          />
        ) : (
          <></>
        )}
        <CopyEvidenceLink
          evidenceInstanceId={evidenceInstanceId}
          controlId={controlId}
          requestId={requestId}
        />
        {canDeleteEvidence && deleteFunc && (
          <EvidenceDeleteButton
            deleteFunc={deleteFunc}
            isMenu={true}
            onClickConfirmationModal={onClickConfirmationModal}
          />
        )}
      </BasicDropdownMenu>
    </Dropdown>
  )
}

export const UnlinkControl = ({
  caption,
  evidenceId,
  control,
}: {
  caption: string
  evidenceId: string
  control: Control.AsObject
}): JSX.Element => {
  const mutate = useGrpcRevalidateByMethod()
  const mutateFunc = async (): Promise<void> => {
    await mutate(AttachmentPromiseClient.prototype.getEvidenceControls)
    await mutate(
      EvidenceManagementPromiseClient.prototype.getEvidenceToControlMappingInfo,
    )
  }
  const unlinkControl = useUnlinkEvidenceFromControl({
    captions: [caption],
    controlId: control.id,
    evidenceIds: [evidenceId],
    controlName: `${control.modelId} ${control.name}`,
    mutateFunc,
  })

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault()
    unlinkControl()
  }

  return (
    <BasicDropdownMenuItem eventKey="2" onClick={onClick}>
      Unlink Control
    </BasicDropdownMenuItem>
  )
}

export const EvidenceControlsListItemMenu = ({
  control,
}: {
  control: Control.AsObject
}): JSX.Element => {
  const { currentEvidenceGroup } = useCurrentEvidenceGroup()
  const hasPermissions = useHasRequiredPermissions([
    PERMISSIONS.READ,
    PERMISSIONS.EDIT,
  ])
  const { setCurrentControl } = useCurrentControl()
  const setCurrentControlHandler = () => {
    setCurrentControl(control)
  }

  if (!hasPermissions || !currentEvidenceGroup) {
    return <></>
  }

  const caption = currentEvidenceGroup.getCaption()
  const evidenceId = currentEvidenceGroup.getEvidenceId()

  return (
    <Dropdown id={`controls-index-menu-${control.id}`}>
      <Dropdown.Toggle
        as={DropdownToggleButton}
        onClickFunc={setCurrentControlHandler}
      >
        <VerticalEllipsesIcon />
      </Dropdown.Toggle>
      <BasicDropdownMenu>
        <ViewDetails />
        <UnlinkControl
          caption={caption}
          evidenceId={evidenceId}
          control={control}
        />
        <CopyLink />
      </BasicDropdownMenu>
    </Dropdown>
  )
}

export type EvidenceDeleteButtonProps = {
  deleteFunc: () => void
  disabled?: boolean
  isMenu?: boolean
  dataId?: string
  onClickConfirmationModal?: (onClick: () => void) => void
}

export const EvidenceDeleteButton = ({
  deleteFunc,
  disabled,
  isMenu,
  dataId,
  onClickConfirmationModal,
}: EvidenceDeleteButtonProps): JSX.Element => {
  if (isMenu) {
    return (
      <BasicDropdownMenuItem
        eventKey="9"
        onClick={() => {
          if (isFunction(onClickConfirmationModal)) {
            onClickConfirmationModal(() => setTimeout(deleteFunc, 0))
          } else {
            deleteFunc()
          }
        }}
        disabled={disabled}
      >
        Delete Evidence
      </BasicDropdownMenuItem>
    )
  }
  return (
    <IconButton
      disabled={disabled}
      variant={IconButtonVariant.icon}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        if (isFunction(onClickConfirmationModal)) {
          onClickConfirmationModal(() => setTimeout(deleteFunc, 0))
        } else {
          deleteFunc()
        }
      }}
      data-id={dataId}
    >
      <DeleteSVG />
    </IconButton>
  )
}

export type EvidenceLinkedControlsCountProps = {
  evidenceInstanceId: string
  controlCount?: number
}

export const EvidenceLinkedControlsCount = ({
  evidenceInstanceId,
  controlCount,
}: EvidenceLinkedControlsCountProps): JSX.Element => {
  const { pageContext } = useParams()
  const evidencePermalink = generatePermalink({
    pageContext: pageContext as string,
    modelType: MODEL_TYPE.EVIDENCE,
    modelId: encodeURIComponent(evidenceInstanceId),
    isInternalLink: true,
    includeSearchParams: true,
    search: parse('tab=linked-controls'),
  })
  return (
    <FlexRow
      $mt={4}
      $mb={4}
      align={FlexAlign.FLEX_START}
      justify={FlexAlign.FLEX_START}
    >
      Evidence has&nbsp;
      <Link to={evidencePermalink} target="_blank" rel="noopener noreferrer">
        {pluralize('linked control', controlCount || 0, true)}
      </Link>
      .
    </FlexRow>
  )
}

export const EvidenceDownloadMenu = ({
  evidence,
}: {
  evidence: EvidenceGroup
}): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const [configs, setConfigs] = useState<EvidenceConfigType[] | null>(null)
  const [isAutomated, setIsAutomated] = useState<boolean>(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const contentId = evidence.getContentId()
  const mime = evidence.getMime()

  const evidenceId = evidence.getId()
  const isLinkedDocument = evidence.getIsLinkedDocument()
  const docImportLink = evidence.getDocImportLink()

  const { data: exclusionsData } = useExclusions(evidenceId)
  const exclusions = exclusionsData?.getExclusions()?.getExclusionsList()
  const isLink = mime === MIME_TYPE.TEXT_URI_LIST

  const caption = evidence.getCaption()
  const isEvidenceAutomated = evidence.getIsAutomated()
  const getEvidenceConfigs = useEvidenceConfigs(
    contentId,
    mime,
    isEvidenceAutomated,
    caption,
  )

  useEffect(() => {
    if (isFunction(getEvidenceConfigs)) {
      const handleConfigs = async () => {
        try {
          const configs = await getEvidenceConfigs()
          if (!configs || !configs.length) return
          setConfigs(configs)
          setIsAutomated(configs.every((config) => config.isAutomated))
        } catch (err) {
          log.error(
            `Error downloading document in EvidenceTabs - id: ${evidence.getId()}`,
            err,
          )
          showInfoToast(ToastPrompts.DOC_DOWNLOAD_ERROR)
        }
      }
      handleConfigs()
    }
  }, [getEvidenceConfigs, evidence, docImportLink])

  const downloadFile = async (): Promise<void> => {
    if (!configs || !configs.length) return
    if (configs.length > 1) {
      const zip = new JSZip()
      configs.forEach((config, index) => {
        if (config.body !== undefined) {
          zip.file(
            config.file?.name || `${caption} ${index}`,
            config.body as string | Uint8Array,
          )
        }
      })
      if (Object.keys(zip.files).length === 0) {
        showInfoToast(ToastPrompts.EVIDENCE_DOWNLOAD_ERROR)
        return
      }
      const content = await zip.generateAsync({ type: 'blob' })
      FileSaver.saveAs(content, `${caption}.zip`)
    } else {
      if (!configs[0].body) return
      const material = documentBodyAsFile(configs[0].body, caption, mime)
      FileSaver.saveAs(material)
    }
  }

  const exclusionsList = useMemo(() => {
    if (exclusions) {
      return exclusions
    }
    return exclusionsData?.getExclusions()?.getExclusionsList() || []
  }, [exclusions, exclusionsData])

  if (isLinkedDocument) return <></>

  const downloadEvidenceAsTable = async (): Promise<void> => {
    if (!configs || !configs.length) return
    if (configs.length > 1) {
      const zip = new JSZip()
      configs.forEach((config, index) => {
        if (!config.body || !(config.body instanceof Uint8Array)) return
        const evidence: Evidence = Evidence.deserializeBinary(config.body)
        const formattedEvidence = formatEvidence({ evidence, asCsv: true })
        zip.file(
          config.file?.name || `${caption} ${index}`,
          formattedEvidence as string | Uint8Array,
        )
      })
      if (Object.keys(zip.files).length === 0) {
        throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
      }
      const content = await zip.generateAsync({ type: 'blob' })
      FileSaver.saveAs(content, `${caption}.zip`)
    } else {
      if (!configs[0].body || !(configs[0].body instanceof Uint8Array)) {
        showInfoToast(ToastPrompts.EVIDENCE_DOWNLOAD_ERROR)
        return
      }
      downloadEvidenceTable(configs[0].body)
    }
  }
  const downloadEvidencePdf = async (): Promise<void> => {
    if (!configs || !configs.length || !exclusionsData) return
    if (configs.length > 1) {
      const zip = new JSZip()
      await Promise.all(
        configs.map(async (config, index) => {
          if (!config.body) return
          const pdfContent = await generateEvidencePdfWithExclusions(
            config.body,
            caption,
            exclusionsList,
          )
          const pdfBuffer = await generatePdfBuffer(pdfContent)
          zip.file(
            config.file?.name || `${caption} ${index}`,
            pdfBuffer as Uint8Array,
          )
        }),
      )
      if (Object.keys(zip.files).length === 0) {
        throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
      }
      const content = await zip.generateAsync({ type: 'blob' })
      FileSaver.saveAs(content, `${caption}.zip`)
    } else {
      if (!configs[0].body) return
      const pdfContent = await generateEvidencePdfWithExclusions(
        configs[0].body,
        caption,
        exclusionsList,
      )
      const pdfBlob = await generatePdfBlob(pdfContent)
      FileSaver.saveAs(pdfBlob, `${caption}.pdf`)
    }
  }
  const onClose = () => {
    if (isOpen) {
      setIsOpen(false)
    }
  }

  return (
    !isLinkedDocument && (
      <>
        <IconButton
          ref={anchorRef}
          variant={IconButtonVariant.icon}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (isOpen) {
              onClose()
            } else {
              setIsOpen(true)
            }
          }}
          className="floating-menu-button"
        >
          <DownloadArrowSVG height={18} width={18} fontSize={18} />
        </IconButton>
        <FloatingMenu
          isOpen={isOpen}
          onClose={onClose}
          menuRef={menuRef}
          anchorElement={anchorRef.current}
          placement={FLOATING_MENU_PLACEMENT.BOTTOM_END}
        >
          {!isAutomated && !isLink && !isLinkedDocument && (
            <FloatingMenuItem
              title={`Download ${pluralize(
                'file',
                configs?.length || 0,
                true,
              )}`}
              onClick={downloadFile}
            />
          )}
          {isAutomated && (
            <>
              <FloatingMenuItem
                title="Download all evidence"
                onClick={downloadEvidencePdf}
              />
              <FloatingMenuItem
                title="Download table"
                onClick={downloadEvidenceAsTable}
              />
            </>
          )}
        </FloatingMenu>
      </>
    )
  )
}

export const ReceptorTemplatesDropDown = ({
  initialSelectedReceptor,
  receptorType,
  onChange,
}: {
  initialSelectedReceptor: Receptor | undefined
  receptorType?: RECEPTOR_TYPE
  onChange: (selectedReceptor: Receptor) => void
}): JSX.Element => {
  const [selectedReceptor, setSelectedReceptor] = useState<Receptor>()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const allReceptors: Receptor[] = getAllReceptorTemplates().filter(
    (receptor) => {
      if (receptorType !== undefined) {
        return receptor.type === receptorType
      }
      return true
    },
  )

  const receptors = allReceptors
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((receptor) =>
      receptor.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )

  useEffect(() => {
    setSelectedReceptor(initialSelectedReceptor)
  }, [initialSelectedReceptor])

  const handleCheckboxChange = (receptor: Receptor) => {
    setSelectedReceptor(receptor)
    onChange(receptor)
  }

  const isReceptorSelected = useCallback(
    (receptor: Receptor): boolean => {
      return selectedReceptor === receptor
    },
    [selectedReceptor],
  )

  return (
    <Dropdown style={{ width: '100%' }}>
      <Dropdown.Toggle
        as={StyledDropdownToggle}
        style={{ width: '100%', height: '40px' }}
      >
        <ReceptorDropdownToggle selectedReceptor={selectedReceptor} />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <div
          style={{ padding: '4px 8px 8px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            placeholder="Search receptors..."
            aria-label="Search receptors"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (
                e.key === 'ArrowUp' ||
                e.key === 'ArrowDown' ||
                e.key === 'Home' ||
                e.key === 'End' ||
                e.key === 'PageUp' ||
                e.key === 'PageDown'
              ) {
                e.stopPropagation()
              }
            }}
            autoFocus
            style={{
              width: '100%',
              padding: '6px 8px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        {receptors.map((receptor) => (
          <Dropdown.Item
            key={receptor.modelid}
            onClick={() => handleCheckboxChange(receptor)}
            active={isReceptorSelected(receptor)}
          >
            <FlexRow
              align={FlexAlign.CENTER}
              justify={FlexAlign.FLEX_START}
              gap={8}
            >
              <ReceptorLogo receptor={receptor} />
              <span>{receptor.name}</span>
            </FlexRow>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  )
}

export const ReceptorLogo = ({
  receptor,
}: {
  receptor: Receptor
}): JSX.Element => {
  const ReceptorIcon = receptor.logo
  return <ReceptorIcon height="24px" width="24px" />
}

export interface ReceptorDropdownToggleProps {
  selectedReceptor: Receptor | undefined
}

export const ReceptorDropdownToggle = ({
  selectedReceptor,
}: ReceptorDropdownToggleProps): JSX.Element => {
  if (!selectedReceptor) {
    return <>Select Receptor</>
  }

  return (
    <FlexRow gap={4}>
      <React.Fragment key={selectedReceptor.tenantid}>
        <FlexRow gap={8}>
          <ReceptorLogo receptor={selectedReceptor} />
          <span>{selectedReceptor.name}</span>
        </FlexRow>
      </React.Fragment>
    </FlexRow>
  )
}
